import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import QRCodeStyling from "qr-code-styling";
import { defaultCardsColor, normalizeColor } from "../../lib/cardTools";

const qrSize = 360;

const createQrCode = ({ url, color }) =>
  new QRCodeStyling({
    width: qrSize,
    height: qrSize,
    data: url,
    margin: 12,
    dotsOptions: {
      color,
      type: "extra-rounded",
    },
    cornersSquareOptions: {
      type: "extra-rounded",
      color,
    },
    cornersDotOptions: {
      type: "dot",
      color,
    },
    backgroundOptions: {
      color: "#ffffff",
    },
  });

const getFileName = (displayName = "cards") =>
  `qr-${displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "") || "cards"}`;

function QrDownloadTool({ data, onTrack }) {
  const containerRef = useRef(null);
  const viewedTrackedRef = useRef(false);
  const [primaryColor, setPrimaryColor] = useState(
    data.primaryColor || defaultCardsColor,
  );
  const [copied, setCopied] = useState(false);
  const normalizedColor = useMemo(
    () => normalizeColor(primaryColor),
    [primaryColor],
  );
  const fileName = useMemo(
    () => getFileName(data.displayName),
    [data.displayName],
  );

  useEffect(() => {
    setPrimaryColor(data.primaryColor || defaultCardsColor);
  }, [data.primaryColor]);

  useEffect(() => {
    if (viewedTrackedRef.current) return;
    viewedTrackedRef.current = true;
    onTrack?.("tool_qr_viewed", {
      hasCardUrl: Boolean(data.cardUrl),
    });
  }, [data.cardUrl, onTrack]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || !data.cardUrl) return undefined;

    node.replaceChildren();
    const qr = createQrCode({
      url: data.cardUrl,
      color: normalizedColor,
    });
    qr.append(node);

    return () => {
      node.replaceChildren();
    };
  }, [data.cardUrl, normalizedColor]);

  const download = (extension) => {
    if (!data.cardUrl) return;

    try {
      createQrCode({
        url: data.cardUrl,
        color: normalizedColor,
      }).download({
        name: fileName,
        extension,
      });
      onTrack?.("tool_qr_download", { extension });
    } catch {
      onTrack?.("tool_qr_download_failed", { extension });
    }
  };

  const copyLink = async () => {
    if (!data.cardUrl) return;

    try {
      await navigator.clipboard.writeText(data.cardUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
      onTrack?.("tool_qr_copy_link");
    } catch {
      setCopied(false);
      onTrack?.("tool_qr_copy_failed", {
        hasCardUrl: Boolean(data.cardUrl),
      });
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,0.8fr)_minmax(280px,1fr)] lg:items-start">
      <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200">
        <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
          Anteprima QR
        </p>
        <div
          ref={containerRef}
          className="mt-4 flex min-h-[260px] items-center justify-center overflow-hidden rounded-lg bg-white p-4 ring-1 ring-slate-200 [&>canvas]:h-auto [&>canvas]:max-w-full [&>svg]:h-auto [&>svg]:max-w-full"
        />
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="qr-primary-color"
            className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500"
          >
            Colore principale
          </label>
          <div className="mt-2 grid grid-cols-[48px_minmax(0,1fr)] overflow-hidden rounded-md border border-slate-200 bg-white focus-within:border-cyan-600 focus-within:ring-2 focus-within:ring-cyan-100">
            <input
              id="qr-primary-color"
              type="color"
              value={normalizedColor}
              onChange={(event) => setPrimaryColor(event.target.value)}
              onBlur={() =>
                onTrack?.("tool_qr_color_changed", {
                  source: "picker",
                })
              }
              className="h-12 w-12 cursor-pointer border-0 bg-transparent p-1"
              aria-label="Colore principale QR code"
            />
            <input
              type="text"
              value={primaryColor}
              onChange={(event) => setPrimaryColor(event.target.value)}
              onBlur={() =>
                onTrack?.("tool_qr_color_changed", {
                  source: "text",
                })
              }
              className="min-w-0 border-0 px-3 py-3 text-sm font-bold text-slate-800 outline-none"
            />
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
            URL collegato
          </p>
          <div className="mt-2 rounded-md bg-slate-50 px-3 py-3 text-xs font-semibold leading-5 text-slate-600 ring-1 ring-slate-200">
            {data.cardUrl || "URL card non disponibile"}
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          <button
            type="button"
            onClick={() => download("png")}
            disabled={!data.cardUrl}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1b8395] px-3 py-3 text-xs font-bold text-white transition hover:bg-[#13526f] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon icon="mdi:file-png-box" className="text-lg" />
            PNG
          </button>
          <button
            type="button"
            onClick={() => download("svg")}
            disabled={!data.cardUrl}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-3 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon icon="mdi:svg" className="text-lg" />
            SVG
          </button>
          <button
            type="button"
            onClick={copyLink}
            disabled={!data.cardUrl}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-3 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon icon={copied ? "mdi:check" : "mdi:content-copy"} className="text-lg" />
            {copied ? "Copiato" : "Link"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QrDownloadTool;
